import { BILLING_INTERVALS, GRANT_DAYS, WEEKDAYS } from '@/config/membershipTierWizardOptions'
import { passSectionCopy, passValid } from '@/config/membershipTierWizardPass'

export { BILLING_INTERVALS, GRANT_DAYS, WEEKDAYS }

export const WIZARD_PATH = '/store/membership-tiers/setup'
export const WIZARD_CAPABILITY = 'membership-tier-wizard'

export const hasTierWizard = user => Boolean(user?.attributes?.capabilities?.includes(WIZARD_CAPABILITY))

export const WIZARD_STEPS = [
  { key: 'name', label: 'Name' },
  { key: 'joining', label: 'Joining' },
  { key: 'includes', label: 'Includes' },
  { key: 'ordering', label: 'Repeat orders' },
  { key: 'mealPlan', label: 'Meal plan' },
  { key: 'pricing', label: 'Member prices' },
  { key: 'packages', label: 'Packages' },
  { key: 'pass', label: 'Pass' },
  { key: 'fees', label: 'Fees' },
  { key: 'review', label: 'Review' }
]

export const INCLUDE_STEPS = { repeat: 'ordering', mealPlan: 'mealPlan', memberPrices: 'pricing' }

const STEP_SHOWN = {
  ordering: data => data.includes.repeat,
  mealPlan: data => data.includes.mealPlan,
  pricing: data => data.includes.memberPrices,
  pass: data => Boolean(data.pass),
  review: (data, context) => !context.editing
}

const shown = (step, data, context) => (STEP_SHOWN[step.key] ? STEP_SHOWN[step.key](data, context) : true)

export const visibleSteps = (data, context = {}) =>
  WIZARD_STEPS.filter(step => shown(step, data, context)).map((step, index) => ({ ...step, index }))

export const questionSteps = (data, context = {}) => visibleSteps(data, context).filter(step => step.key !== 'review')

export const stepForKey = key => WIZARD_STEPS.find(s => s.key === key)

export const progressLabel = (index, total) => (index < total ? `Step ${index + 1} of ${total}` : '')

export const tierLabel = data => data.name.trim() || 'this membership'

const tierSubject = data => data.name.trim() || 'This membership'

export const customersOf = data => (data.name.trim() ? `${data.name.trim()} customers` : 'customers on this membership')

const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1)

const listOf = items =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0]

const wording = data => ({
  name: tierLabel(data),
  subject: tierSubject(data),
  customers: customersOf(data),
  Customers: capitalize(customersOf(data))
})

const HEADINGS = {
  name: () => 'What is this membership called?',
  joining: ({ name }) => `How do customers get onto ${name}?`,
  includes: ({ name }) => `What does ${name} include?`,
  ordering: ({ name }) => `How do ${name} orders repeat?`,
  mealPlan: ({ name }) => `What's in the ${name} meal plan?`,
  pricing: ({ customers }) => `What do ${customers} pay for products?`,
  packages: ({ customers }) => `Which packages can ${customers} pick?`,
  pass: ({ customers }) => `What do ${customers} get with their pass?`,
  fees: ({ customers }) => `Do ${customers} pay a membership fee?`,
  review: ({ name }) => `Ready to create ${name}?`
}

export const stepHeading = (key, data) => HEADINGS[key](wording(data))

const SUBTITLES = {
  fees: "Fees are added to the order total and listed under 'Taxes & Fees' at checkout. Leave both off for a free membership.",
  review: "Everything here can be changed on the membership's page afterwards."
}

export const stepSubtitle = key => SUBTITLES[key] ?? ''

export const DESCRIPTION_HELP =
  "Customers see this description under the membership name when they choose. A long description is cut off behind 'Show more...'."

export const startingTierHelp = (data, currentDefault) => {
  const { name, subject } = wording(data)
  const help =
    data.joining.mode === 'invite'
      ? `Customers who are on no other membership are placed on ${name}, for example when a meal plan ends or when they sign up through a popup.`
      : `New customers in the stores you picked start on ${name} before they choose anything, and ${name} is already ticked for them under 'Order Frequency?' at checkout.`
  if (!currentDefault) return help
  return `${help} ${subject} takes over from '${currentDefault.name}' as the starting membership.`
}

const SECTIONS = {
  details: () => ({
    title: 'Name and description',
    description:
      "Customers see this name under 'Order Frequency?' at checkout when they choose a membership, so describe how they order, for example 'Weekly Recurring Order'."
  }),
  joining: ({ name }) => ({
    title: 'How they join',
    description: `Choose whether customers pick ${name} for themselves at checkout, or only you move customers onto ${name}.`
  }),
  stores: ({ name }) => ({
    title: 'Stores',
    description: `The stores that list ${name} under 'Order Frequency?' at checkout.`
  }),
  starting: (words, data) => ({ title: 'Starting membership', description: startingTierHelp(data, null) }),
  upgrades: ({ name, Customers }) => ({
    title: 'Upgrades and downgrades',
    description: `Link ${name} to your other memberships: the memberships ${Customers} can upgrade to, and the one they drop back to.`
  }),
  includes: ({ name }) => ({
    title: 'What the membership includes',
    description: `Tick everything that comes with ${name}. Leave all three unticked for a membership that only groups customers, for example for fees or automations.`
  }),
  every: ({ customers }) => ({
    title: 'How often',
    description: `How often each order from ${customers} repeats. Bottle sets up the next order on this schedule.`
  }),
  payment: ({ customers }) => ({ title: 'Payment', description: `How each repeat order from ${customers} is paid.` }),
  subscriptionStores: ({ Customers }) => ({
    title: 'Stores for repeat orders',
    description: `The stores ${Customers} can set up repeat orders from, and the store picked for them when they start.`
  }),
  minimumOrders: ({ Customers }) => ({
    title: 'Minimum orders',
    description: `How many paid orders ${Customers} place before they can skip a repeat order. 1 means they can skip from the start. A change applies to repeat orders set up after you save.`
  }),
  mealPlanPrice: ({ customers }) => ({
    title: 'Price and billing',
    description: `What ${customers} pay for the meal plan, billed on this cadence for as long as they stay on the membership.`
  }),
  mealPlanMeals: ({ customers }) => ({
    title: 'Included meals',
    description: `How many meals ${customers} get each cycle, and the category they pick those meals from.`
  }),
  mealPlanGrants: () => ({
    title: 'When meals are granted',
    description: 'How often a new batch of included meals is added, and on which day or date.'
  }),
  mealPlanExpiry: () => ({
    title: 'Unused meals',
    description: 'What happens to included meals a customer has not used when the next batch arrives.'
  }),
  packages: () => ({
    title: 'Packages',
    description:
      "Choose from the packages you've already created in your stores. A package sets minimum order requirements and autofill rules for an order."
  }),
  newPackages: ({ customers }) => ({
    title: 'New packages',
    description: `Create a package that only ${customers} can pick, or one open to every membership. Autofill rules, category minimums and automatic variants are set later in the store's Packages section.`
  }),
  packagesSeen: ({ customers }) => ({
    title: 'What customers see',
    description: `The packages ${customers} can pick at checkout once you save.`
  }),
  prices: ({ name }) => ({
    title: 'Product prices',
    description: `Sets the ${name} price of every product in every store, from each product's regular price.`
  }),
  percent: ({ name, Customers }) => ({
    title: 'Percentage off',
    description: `The percentage taken off each product's regular price for ${Customers}. Each ${name} price is worked out once, when you save.`
  }),
  amount: ({ name, Customers }) => ({
    title: 'Amount off',
    description: `The amount taken off each product's regular price for ${Customers}, never below zero. Each ${name} price is worked out once, when you save.`
  }),
  exceptions: ({ customers }) => ({
    title: 'Exceptions',
    description: `Two things can change what ${customers} pay for a product.`
  }),
  signUpFee: ({ name }) => ({
    title: 'Sign-up fee',
    description: `Charged once, on the first order a customer places on ${name}.`
  }),
  recurringFee: ({ customers }) => ({
    title: 'Recurring fee',
    description: `Charged again as ${customers} keep ordering: every set number of orders, or every set amount spent.`
  }),
  summary: () => ({ title: 'Summary', description: 'What you are about to create.' }),
  notIncluded: () => ({ title: 'Good to know', description: 'What happens after you create the membership.' })
}

export const sectionCopy = (key, data) => SECTIONS[key](wording(data), data)

const passName = data => data.pass?.name?.trim() || 'this pass'

export const passCopy = (key, data) => passSectionCopy(key, { ...wording(data), passName: passName(data) })

export const newPackage = storeId => ({
  name: '',
  description: '',
  storeId,
  minimumItems: 0,
  minimumPriceCents: 0,
  openToAll: false
})

export const joiningOptions = data => {
  const { name, subject } = wording(data)
  return [
    {
      value: 'checkout',
      title: `Customers choose ${name} at checkout`,
      subtitle: `${subject} is listed under 'Order Frequency?' in the stores you pick.`
    },
    {
      value: 'invite',
      title: 'Only customers you add',
      subtitle: `${subject} is not offered at checkout. You move customers onto ${name} from their profile or with an automation.`
    }
  ]
}

const customerCount = count => `${count} ${count === 1 ? 'customer' : 'customers'}`

export const removalWarnings = (data, meta = {}) => {
  const { name, customers } = wording(data)
  const warnings = []
  const mealPlanMembers = meta.meal_plan_member_count ?? 0
  if (!data.includes.mealPlan && mealPlanMembers > 0) {
    warnings.push(
      `Saving with A meal plan unticked removes the ${name} meal plan and cancels the meal plan of ${customerCount(mealPlanMembers)}.`
    )
  }
  if (data.pass && !data.pass.linked) {
    warnings.push(`Saving with the pass switched off unlinks ${passName(data)} from ${name}. The pass itself is kept.`)
  }
  if (!data.includes.memberPrices && data.pricing.startedWithMemberPrices) {
    warnings.push(`Saving with Member prices unticked sets every product back to its regular price for ${customers}.`)
  }
  return warnings
}

export const upgradeNote = (data, tier, tiers, editingId = null) => {
  if (!tier.downgradeId || tier.downgradeId === editingId) return ''
  const current = tiers.find(t => t.id === tier.downgradeId)
  if (!current) return ''
  return `Drops back to ${current.name} today. Ticking ${tier.name} makes ${tier.name} drop back to ${tierLabel(data)} instead.`
}

export const includeOptions = data => {
  const { customers, Customers } = wording(data)
  return [
    {
      key: 'repeat',
      title: 'Repeat orders',
      subtitle: `Bottle sets up the next order for ${customers} on a schedule, as a reminder or charged automatically.`
    },
    {
      key: 'mealPlan',
      title: 'A meal plan',
      subtitle: `${Customers} pay a set price each cycle and pick their included meals.`
    },
    {
      key: 'memberPrices',
      title: 'Member prices',
      subtitle: `A percentage or an amount off every product for ${customers}.`
    }
  ]
}

export const INTERVAL_UNITS = [
  { name: 'days', value: 'days' },
  { name: 'weeks', value: 'weeks' },
  { name: 'months', value: 'months' },
  { name: 'years', value: 'years' }
]

const UNIT_VALUES = INTERVAL_UNITS.map(u => u.value)

export const INTERVAL_PRESETS = [
  { unit: 'weeks', count: 1 },
  { unit: 'weeks', count: 2 },
  { unit: 'months', count: 1 }
]

export const WEEKDAY_SCHEDULE = 'custom-week'

const weekdayOrder = value => (value === 0 ? 7 : value)

const sortedWeekdays = weekdays => [...weekdays].sort((a, b) => weekdayOrder(a) - weekdayOrder(b))

export const paymentOptions = data => {
  const options = [
    {
      value: 'always',
      title: 'Charged automatically',
      subtitle: `The customer's saved card is charged for each repeat order unless they skip that order. ${tierSubject(data)} shows an 'Autocharged' label at checkout.`
    },
    {
      value: 'never',
      title: 'Customers check out each time',
      subtitle: 'Customers are reminded and pay for each repeat order themselves.'
    }
  ]
  if (!data.ordering.allowChoice) return options
  return [
    ...options,
    {
      value: 'choice',
      title: 'Customers choose',
      subtitle: `Set on ${tierLabel(data)} before this wizard. Repeat orders are charged automatically today.`
    }
  ]
}

const BILLING_VALUES = BILLING_INTERVALS.map(i => i.value)
const GRANT_DAY_VALUES = GRANT_DAYS.map(d => d.value)
const LAST_GRANT_DATE = 31

const EXPIRY_UNITS = { day: 'days', week: 'weeks', month: 'months', year: 'years' }

export const expiryOptions = data => {
  const options = [
    { value: 'week', title: 'Expire at the end of each week' },
    { value: 'month', title: 'Roll over for a month' },
    { value: 'never', title: 'Never expire' }
  ]
  const custom = data.mealPlan.customExpiry
  if (!custom) return options
  const unit = custom.value === 1 ? custom.type : EXPIRY_UNITS[custom.type] ?? `${custom.type}s`
  return [
    ...options,
    { value: 'custom', title: `Keep the current setting: unused meals expire after ${custom.value} ${unit}` }
  ]
}

export const pricingOptions = (data, { customPriceCount = 0 } = {}) => {
  const { name } = wording(data)
  const options = [
    {
      value: 'percent',
      title: 'A percentage off every product',
      subtitle: "Each product's member price is its regular price less the percentage."
    },
    {
      value: 'amount',
      title: 'An amount off every product',
      subtitle: "Each product's member price is its regular price less the amount, never below $0."
    }
  ]
  if (!data.pricing.startedWithMemberPrices) return options
  return [
    {
      value: 'keep',
      title: 'Keep the current member prices',
      subtitle: `${customPriceCount} products have their own ${name} price today.`
    },
    ...options
  ]
}

export const PRICING_STACKING_NOTE =
  "A store's order-size rewards still apply on top for products marked 'Subject to pricing tiers', when the reward is open to this membership."

export const FEE_INTERVAL_TYPES = [
  { name: 'orders', value: 'order_amount' },
  { name: 'dollars spent', value: 'dollar_amount' }
]

export const DEFAULT_FEE_NAMES = { signUp: 'Sign-up fee', recurring: 'Membership fee' }

const isWeekdaySchedule = interval => interval.unit === WEEKDAY_SCHEDULE

export const intervalKey = interval =>
  isWeekdaySchedule(interval)
    ? `${WEEKDAY_SCHEDULE}:${sortedWeekdays(interval.weekdays).join('')}`
    : `${interval.unit}:${interval.count}`

export const intervalAmount = ({ unit, count }) => `${count} ${count === 1 ? unit.slice(0, -1) : unit}`

const weekdayNames = weekdays =>
  sortedWeekdays(weekdays).map(value => WEEKDAYS.find(day => day.value === value)?.long ?? '')

export const intervalLabel = interval =>
  isWeekdaySchedule(interval) ? `Every ${listOf(weekdayNames(interval.weekdays))}` : `Every ${intervalAmount(interval)}`

const wholeNumber = value => Number.isInteger(value) && value >= 1

export const CUSTOM_INTERVAL = 'custom'
export const WEEKDAYS_INTERVAL = 'weekdays'

const DAYS_PER_WEEK = 7

export const customValid = custom =>
  isWeekdaySchedule(custom)
    ? Array.isArray(custom.weekdays) && custom.weekdays.length > 0
    : wholeNumber(custom.count) && UNIT_VALUES.includes(custom.unit)

const presetFor = key => INTERVAL_PRESETS.find(preset => intervalKey(preset) === key)

const sameAsPreset = ({ unit, count }, preset) =>
  (unit === preset.unit && count === preset.count) ||
  (unit === 'days' && preset.unit === 'weeks' && count === preset.count * DAYS_PER_WEEK)

export const matchingPreset = interval =>
  isWeekdaySchedule(interval) ? undefined : INTERVAL_PRESETS.find(preset => sameAsPreset(interval, preset))

export const canonicalInterval = (interval, { snap = true } = {}) => {
  if (isWeekdaySchedule(interval)) return { unit: WEEKDAY_SCHEDULE, weekdays: sortedWeekdays(interval.weekdays) }
  const preset = snap && matchingPreset(interval)
  return preset ? { ...preset } : { unit: interval.unit, count: interval.count }
}

export const defaultInterval = (data, options = {}) => {
  const { every, custom, weekdays } = data.ordering
  if (every === WEEKDAYS_INTERVAL) {
    return weekdays.length ? { unit: WEEKDAY_SCHEDULE, weekdays: sortedWeekdays(weekdays) } : undefined
  }
  if (every !== CUSTOM_INTERVAL) return presetFor(every)
  return customValid(custom) ? canonicalInterval(custom, options) : undefined
}

export const extraIntervalOptions = data =>
  INTERVAL_PRESETS.filter(preset => intervalKey(preset) !== data.ordering.every)

const uniqueIntervals = intervals =>
  intervals.filter((interval, index) => intervals.findIndex(i => intervalKey(i) === intervalKey(interval)) === index)

const extraIntervals = (data, options) => {
  if (!data.ordering.canSwitch) return []
  const presets = extraIntervalOptions(data).filter(preset => data.ordering.alsoOffer.includes(intervalKey(preset)))
  const customs = data.ordering.customExtras
    .filter(extra => options.keepInvalid || customValid(extra))
    .map(extra => canonicalInterval(extra, options))
  return [...presets, ...customs]
}

export const selectedIntervals = (data, options = {}) => {
  const start = defaultInterval(data, options)
  return uniqueIntervals([...(start ? [start] : []), ...extraIntervals(data, options)])
}

export const switchCopy = data => {
  const start = defaultInterval(data)
  const where = "They pick under 'Interval' at checkout or in their account."
  return {
    label: `Let ${customersOf(data)} change how often`,
    help: start ? `${where} Everyone starts on ${intervalLabel(start).toLowerCase()}.` : where
  }
}

export const defaultWizardData = () => ({
  name: '',
  description: '',
  joining: { mode: null, storeIds: [], isDefault: false, downgradeTierId: null, upgradeTierIds: [] },
  includes: { repeat: false, mealPlan: false, memberPrices: false },
  ordering: {
    every: 'weeks:1',
    custom: { count: 10, unit: 'days' },
    weekdays: [],
    canSwitch: false,
    alsoOffer: [],
    customExtras: [],
    autocharge: 'always',
    allowChoice: false,
    subscriptionStoreIds: null,
    defaultSubscriptionStoreId: null,
    minimumOrders: 1,
    startWithoutDefault: null
  },
  mealPlan: {
    priceCents: null,
    billingInterval: 'weekly',
    itemCount: 5,
    categoryId: null,
    grantInterval: 'weekly',
    grantDay: 'monday',
    grantDate: 1,
    expiry: 'week',
    customExpiry: null
  },
  pricing: { mode: null, percent: null, amountCents: null, startedWithMemberPrices: false },
  packageIds: [],
  newPackages: [],
  pass: null,
  fees: {
    signUp: { enabled: false, name: DEFAULT_FEE_NAMES.signUp, amountCents: null },
    recurring: {
      enabled: false,
      name: DEFAULT_FEE_NAMES.recurring,
      amountCents: null,
      intervalType: 'order_amount',
      every: null
    }
  }
})

const PERCENT_DECIMALS = 100
const FULL_PERCENT = 100

export const percentValid = percent => {
  if (percent === null || percent === '') return false
  const n = Number(percent)
  if (!Number.isFinite(n) || n <= 0 || n >= FULL_PERCENT) return false
  return Math.abs(n * PERCENT_DECIMALS - Math.round(n * PERCENT_DECIMALS)) < 1e-6
}

const amountValid = cents => Number.isInteger(cents) && cents > 0

export const signUpFeeValid = fee => !fee.enabled || amountValid(fee.amountCents)

export const recurringFeeValid = fee => !fee.enabled || (amountValid(fee.amountCents) && wholeNumber(fee.every))

const subscriptionStoresValid = ({ subscriptionStoreIds, defaultSubscriptionStoreId }) => {
  if (subscriptionStoreIds === null) return true
  if (!subscriptionStoreIds.length) return false
  return defaultSubscriptionStoreId === null || subscriptionStoreIds.includes(defaultSubscriptionStoreId)
}

const intervalsValid = ({ ordering, ...data }) =>
  Boolean(defaultInterval({ ordering, ...data })) && (!ordering.canSwitch || ordering.customExtras.every(customValid))

const minimumOrdersValid = ({ ordering }) => wholeNumber(ordering.minimumOrders)

const orderingValid = data => intervalsValid(data) && subscriptionStoresValid(data.ordering) && minimumOrdersValid(data)

const grantValid = ({ grantInterval, grantDay, grantDate }) =>
  grantInterval === 'monthly'
    ? Number.isInteger(grantDate) && grantDate >= 1 && grantDate <= LAST_GRANT_DATE
    : GRANT_DAY_VALUES.includes(grantDay)

const mealPlanValid = ({ mealPlan }) =>
  Number.isInteger(mealPlan.priceCents) &&
  mealPlan.priceCents >= 0 &&
  wholeNumber(mealPlan.itemCount) &&
  Boolean(mealPlan.categoryId) &&
  BILLING_VALUES.includes(mealPlan.billingInterval) &&
  BILLING_VALUES.includes(mealPlan.grantInterval) &&
  grantValid(mealPlan)

const pricingValid = ({ pricing }) => {
  if (pricing.mode === 'percent') return percentValid(pricing.percent)
  if (pricing.mode === 'amount') return amountValid(pricing.amountCents)
  return pricing.mode === 'keep'
}

const wholeOrZero = value => Number.isInteger(value) && value >= 0

const newPackageValid = pkg =>
  pkg.name.trim().length > 0 &&
  Boolean(pkg.storeId) &&
  wholeOrZero(pkg.minimumItems) &&
  wholeOrZero(pkg.minimumPriceCents)

const VALIDATORS = {
  name: data => data.name.trim().length > 0,
  joining: ({ joining }) => joining.mode === 'invite' || (joining.mode === 'checkout' && joining.storeIds.length > 0),
  includes: () => true,
  ordering: orderingValid,
  mealPlan: mealPlanValid,
  pricing: pricingValid,
  packages: data => data.newPackages.every(newPackageValid),
  pass: data => passValid(data.pass),
  fees: ({ fees }) => signUpFeeValid(fees.signUp) && recurringFeeValid(fees.recurring)
}

export const stepValid = (key, data) => (VALIDATORS[key] ? VALIDATORS[key](data) : false)

export const STEP_PARTS = {
  name: [{ keys: ['name', 'description'], valid: VALIDATORS.name }],
  joining: [
    { keys: ['joining', 'store_ids', 'is_default', 'downgrade_tier_id', 'upgrade_tier_ids'], valid: VALIDATORS.joining }
  ],
  ordering: [
    { keys: ['recurring', 'intervals'], valid: intervalsValid },
    {
      keys: ['subscription_store_ids', 'default_subscription_store_id'],
      valid: data => subscriptionStoresValid(data.ordering)
    },
    { keys: ['minimum_order_count'], valid: minimumOrdersValid }
  ],
  mealPlan: [{ keys: ['meal_plan'], valid: mealPlanValid }],
  pricing: [{ keys: ['pricing'], valid: pricingValid }],
  packages: [{ keys: ['package_ids', 'new_packages'], valid: VALIDATORS.packages }],
  pass: [{ keys: ['pass'], valid: VALIDATORS.pass }],
  fees: [
    { keys: ['sign_up_fee'], valid: ({ fees }) => signUpFeeValid(fees.signUp) },
    { keys: ['recurring_fee'], valid: ({ fees }) => recurringFeeValid(fees.recurring) }
  ]
}

export const allStepsValid = (data, context = {}) =>
  questionSteps(data, context).every(step => stepValid(step.key, data))
